import {
  normalizeSearchExpressionOperator,
  searchExpressionNodeKey,
} from "./core.js";

function isExpressionNode(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function expressionChildren(node) {
  if (node?.type === "group" && Array.isArray(node.children)) {
    return node.children.filter(isExpressionNode);
  }
  if (node?.type === "container" && isExpressionNode(node.expression)) {
    return [node.expression];
  }
  return [];
}

function leafPaths(node, path, output = []) {
  if (!isExpressionNode(node)) {
    return output;
  }
  if (node.type === "term") {
    output.push({ node, path });
    return output;
  }
  expressionChildren(node).forEach((child, index) => {
    leafPaths(child, `${path}.${index}`, output);
  });
  return output;
}

function normalizedSpan(node, sourceLength) {
  const start = node?.span?.start;
  const end = node?.span?.end;
  if (
    !Number.isInteger(start) ||
    !Number.isInteger(end) ||
    start < 0 ||
    end <= start ||
    start >= sourceLength ||
    end > sourceLength
  ) {
    return null;
  }
  return {
    start,
    end,
  };
}

function addBinding(bindings, source, rawNode, expressionPath, rawPath = expressionPath) {
  const span = normalizedSpan(rawNode, source.length);
  if (!span || !expressionPath) {
    return;
  }
  const sourceText = source.slice(span.start, span.end);
  if (!sourceText) {
    return;
  }
  bindings.set(expressionPath, {
    sourceText,
    sourceStart: span.start,
    sourceEnd: span.end,
    rawPath,
  });
}

function addProjectedBinding(bindings, expressionPath, binding) {
  if (!expressionPath || !binding || bindings.has(expressionPath)) {
    return;
  }
  bindings.set(expressionPath, { ...binding });
}

const RESOLVED_BINDINGS_VERSION = 1;
const NODE_PATH_PATTERN = /^root(?:\.\d+)*$/u;
const OPERATOR_PATH_PATTERN = /^(root(?:\.\d+)*)::operator:(\d+)$/u;
const BINDING_PATH_PATTERN = /^root(?:\.\d+)*(?:::operator:\d+)?$/u;

export function searchExpressionOperatorBindingKey(groupPath, boundaryIndex) {
  const path = String(groupPath ?? "").trim();
  const index = Number.parseInt(boundaryIndex, 10);
  return path && Number.isInteger(index) && index > 0
    ? `${path}::operator:${index}`
    : "";
}

function addOperatorBinding(
  bindings,
  source,
  leftRawNode,
  rightRawNode,
  groupPath,
  boundaryIndex,
  rawGroupPath = groupPath,
  rawBoundaryIndex = boundaryIndex,
) {
  const key = searchExpressionOperatorBindingKey(groupPath, boundaryIndex);
  const leftSpan = normalizedSpan(leftRawNode, source.length);
  const rightSpan = normalizedSpan(rightRawNode, source.length);
  if (!key || !leftSpan || !rightSpan || rightSpan.start < leftSpan.end) {
    return;
  }
  const gap = source.slice(leftSpan.end, rightSpan.start);
  if (!gap) {
    return;
  }
  const leadingWhitespace = gap.match(/^\s*/u)?.[0].length ?? 0;
  const trailingWhitespace = gap.match(/\s*$/u)?.[0].length ?? 0;
  const hasLiteralOperator = leadingWhitespace + trailingWhitespace < gap.length;
  const sourceStart = hasLiteralOperator
    ? leftSpan.end + leadingWhitespace
    : leftSpan.end;
  const sourceEnd = hasLiteralOperator
    ? rightSpan.start - trailingWhitespace
    : rightSpan.start;
  bindings.set(key, {
    sourceText: source.slice(sourceStart, sourceEnd),
    sourceStart,
    sourceEnd,
    implicit: !hasLiteralOperator,
    rawPath: searchExpressionOperatorBindingKey(rawGroupPath, rawBoundaryIndex),
  });
}

function alignNodes(
  bindings,
  source,
  rawNode,
  expressionNode,
  expressionPath,
  rawPath = expressionPath,
) {
  if (!isExpressionNode(rawNode) || !isExpressionNode(expressionNode)) {
    return;
  }

  if (rawNode.type === "term") {
    if (expressionNode.type === "term") {
      addBinding(bindings, source, rawNode, expressionPath, rawPath);
      return;
    }
    const expressionLeaves = leafPaths(expressionNode, expressionPath);
    if (expressionLeaves.length === 1) {
      addBinding(bindings, source, rawNode, expressionLeaves[0].path, rawPath);
      return;
    }
    // A single literal can intentionally expand into a semantic group. For
    // example, `patch:2026-03-12` becomes matching from/to bounds. Keep that
    // expansion under one source-bearing unit instead of pretending that the
    // literal was typed twice.
    addBinding(bindings, source, rawNode, expressionPath, rawPath);
    return;
  }

  if (rawNode.type === "container" || expressionNode.type === "container") {
    if (rawNode.type !== "container" || expressionNode.type !== "container") {
      addBinding(bindings, source, rawNode, expressionPath, rawPath);
      return;
    }
    alignNodes(
      bindings,
      source,
      rawNode.expression,
      expressionNode.expression,
      `${expressionPath}.0`,
      `${rawPath}.0`,
    );
    return;
  }

  if (expressionNode.type === "term") {
    // Resolution can also compact several equivalent literals. In that case
    // the full raw group is the honest source for the surviving semantic node.
    addBinding(bindings, source, rawNode, expressionPath, rawPath);
    return;
  }

  const rawChildren = expressionChildren(rawNode);
  const expressionNodeChildren = expressionChildren(expressionNode);
  if (rawChildren.length === expressionNodeChildren.length) {
    for (let index = 1; index < rawChildren.length; index += 1) {
      addOperatorBinding(
        bindings,
        source,
        rawChildren[index - 1],
        rawChildren[index],
        expressionPath,
        index,
        rawPath,
        index,
      );
    }
    rawChildren.forEach((rawChild, index) => {
      alignNodes(
        bindings,
        source,
        rawChild,
        expressionNodeChildren[index],
        `${expressionPath}.${index}`,
        `${rawPath}.${index}`,
      );
    });
    return;
  }

  // A normalized legacy tree may no longer have enough information to align
  // unequal shapes. Never zip leaves by count: expansion and dedup can cancel
  // numerically while referring to different meanings.
  addBinding(bindings, source, rawNode, expressionPath, rawPath);
}

function collectExpressionBindingTargets(node, path = "root", targets = new Map()) {
  if (!isExpressionNode(node)) {
    return targets;
  }
  targets.set(path, {
    kind: "node",
    node,
    targetKey: searchExpressionNodeKey(node),
  });
  if (node.type === "container") {
    collectExpressionBindingTargets(node.expression, `${path}.0`, targets);
    return targets;
  }
  if (node.type !== "group") {
    return targets;
  }
  const children = expressionChildren(node);
  children.forEach((child, index) => {
    if (index > 0) {
      targets.set(searchExpressionOperatorBindingKey(path, index), {
        kind: "operator",
        group: node,
        targetKey: `operator:${normalizeSearchExpressionOperator(node.operator)}`,
      });
    }
    collectExpressionBindingTargets(child, `${path}.${index}`, targets);
  });
  return targets;
}

export function normalizeSearchExpressionSourceBindingSidecar(value) {
  if (
    !isExpressionNode(value) ||
    value.version !== RESOLVED_BINDINGS_VERSION ||
    typeof value.source !== "string" ||
    typeof value.expressionKey !== "string" ||
    !value.expressionKey ||
    !Array.isArray(value.entries)
  ) {
    return null;
  }
  const entries = [];
  for (const entry of value.entries) {
    const expressionPath = String(entry?.expressionPath ?? "").trim();
    const rawPath = String(entry?.rawPath ?? "").trim();
    const targetKey = String(entry?.targetKey ?? "").trim();
    const sourceText = entry?.sourceText;
    const sourceStart = entry?.sourceStart;
    const sourceEnd = entry?.sourceEnd;
    if (
      !BINDING_PATH_PATTERN.test(expressionPath) ||
      !BINDING_PATH_PATTERN.test(rawPath) ||
      !targetKey ||
      typeof sourceText !== "string" ||
      !sourceText ||
      !Number.isInteger(sourceStart) ||
      !Number.isInteger(sourceEnd) ||
      sourceStart < 0 ||
      sourceEnd <= sourceStart ||
      sourceEnd > value.source.length ||
      sourceText !== value.source.slice(sourceStart, sourceEnd)
    ) {
      return null;
    }
    entries.push({
      expressionPath,
      rawPath,
      targetKey,
      sourceText,
      sourceStart,
      sourceEnd,
      ...(entry?.implicit === true || entry?.implicit === false
        ? { implicit: entry.implicit }
        : {}),
    });
  }
  return {
    version: RESOLVED_BINDINGS_VERSION,
    source: value.source,
    expressionKey: value.expressionKey,
    entries,
  };
}

function expressionNodeAtPath(root, path) {
  if (!NODE_PATH_PATTERN.test(path)) {
    return null;
  }
  let node = root;
  for (const segment of path.split(".").slice(1)) {
    const index = Number.parseInt(segment, 10);
    const children = expressionChildren(node);
    if (!Number.isInteger(index) || index < 0 || index >= children.length) {
      return null;
    }
    node = children[index];
  }
  return isExpressionNode(node) ? node : null;
}

function rawOwnerForPath(source, rawExpressionTree, rawPath) {
  const operatorMatch = OPERATOR_PATH_PATTERN.exec(rawPath);
  if (!operatorMatch) {
    const node = expressionNodeAtPath(rawExpressionTree, rawPath);
    const span = normalizedSpan(node, source.length);
    return span ? { kind: "node", node, ...span } : null;
  }
  const groupPath = operatorMatch[1];
  const boundaryIndex = Number.parseInt(operatorMatch[2], 10);
  const group = expressionNodeAtPath(rawExpressionTree, groupPath);
  const children = expressionChildren(group);
  if (
    group?.type !== "group" ||
    !Number.isInteger(boundaryIndex) ||
    boundaryIndex <= 0 ||
    boundaryIndex >= children.length
  ) {
    return null;
  }
  const candidate = new Map();
  addOperatorBinding(
    candidate,
    source,
    children[boundaryIndex - 1],
    children[boundaryIndex],
    groupPath,
    boundaryIndex,
  );
  const binding = candidate.get(rawPath);
  return binding ? { kind: "operator", group, binding } : null;
}

function bindingsCoverExpression(node, path, bindings) {
  if (bindings.has(path)) {
    return true;
  }
  if (node?.type === "container") {
    return bindingsCoverExpression(node.expression, `${path}.0`, bindings);
  }
  if (node?.type !== "group") {
    return false;
  }
  const children = expressionChildren(node);
  return children.every((child, index) =>
    bindingsCoverExpression(child, `${path}.${index}`, bindings) &&
    (
      index === 0 ||
      bindings.has(searchExpressionOperatorBindingKey(path, index))
    )
  );
}

function resolvedBindingsFromRawTree(source, rawExpressionTree, expression) {
  if (!Object.hasOwn(rawExpressionTree, "resolvedBindings")) {
    return null;
  }
  const sidecar = normalizeSearchExpressionSourceBindingSidecar(
    rawExpressionTree.resolvedBindings,
  );
  if (
    !sidecar ||
    sidecar.source !== source ||
    sidecar.expressionKey !== searchExpressionNodeKey(expression) ||
    !Array.isArray(sidecar.entries)
  ) {
    return new Map();
  }
  const targets = collectExpressionBindingTargets(expression);
  const bindings = new Map();
  const ranges = [];
  const rawPaths = new Set();
  for (const entry of sidecar.entries) {
    const expressionPath = String(entry?.expressionPath ?? "").trim();
    const rawPath = String(entry?.rawPath ?? "").trim();
    const sourceStart = entry?.sourceStart;
    const sourceEnd = entry?.sourceEnd;
    const target = targets.get(expressionPath);
    const operatorBinding = target?.kind === "operator";
    const hasImplicit = entry?.implicit === true || entry?.implicit === false;
    if (
      !target ||
      entry.targetKey !== target.targetKey ||
      entry.sourceText !== source.slice(sourceStart, sourceEnd) ||
      (!NODE_PATH_PATTERN.test(rawPath) && !OPERATOR_PATH_PATTERN.test(rawPath)) ||
      !Number.isInteger(sourceStart) ||
      !Number.isInteger(sourceEnd) ||
      sourceStart < 0 ||
      sourceEnd <= sourceStart ||
      sourceEnd > source.length ||
      bindings.has(expressionPath) ||
      rawPaths.has(rawPath) ||
      operatorBinding !== hasImplicit
    ) {
      return new Map();
    }
    const rawOwner = rawOwnerForPath(source, rawExpressionTree, rawPath);
    if (
      !rawOwner ||
      rawOwner.kind !== target.kind ||
      (
        rawOwner.kind === "node" &&
        (
          rawOwner.node?.type !== "term" ||
          rawOwner.start !== sourceStart ||
          rawOwner.end !== sourceEnd
        )
      ) ||
      (
        rawOwner.kind === "operator" &&
        (
          rawOwner.binding.sourceStart !== sourceStart ||
          rawOwner.binding.sourceEnd !== sourceEnd ||
          rawOwner.binding.implicit !== entry.implicit ||
          normalizeSearchExpressionOperator(rawOwner.group.operator) !==
            normalizeSearchExpressionOperator(target.group.operator)
        )
      )
    ) {
      return new Map();
    }
    const sourceText = source.slice(sourceStart, sourceEnd);
    if (!sourceText) {
      return new Map();
    }
    const binding = { sourceText, sourceStart, sourceEnd };
    if (hasImplicit) {
      binding.implicit = entry.implicit;
    }
    bindings.set(expressionPath, binding);
    rawPaths.add(rawPath);
    ranges.push({ sourceStart, sourceEnd });
  }
  ranges.sort((left, right) =>
    left.sourceStart - right.sourceStart || left.sourceEnd - right.sourceEnd
  );
  for (let index = 1; index < ranges.length; index += 1) {
    if (ranges[index].sourceStart < ranges[index - 1].sourceEnd) {
      return new Map();
    }
  }
  return bindingsCoverExpression(expression, "root", bindings)
    ? bindings
    : new Map();
}

function visitExpressionGroups(node, path, visit) {
  if (!isExpressionNode(node)) {
    return;
  }
  if (node.type === "container") {
    visitExpressionGroups(node.expression, `${path}.0`, visit);
    return;
  }
  if (node.type !== "group") {
    return;
  }
  const children = expressionChildren(node);
  visit(node, path, children);
  children.forEach((child, index) => {
    visitExpressionGroups(child, `${path}.${index}`, visit);
  });
}

function immediateOriginChildIndex(parentPath, childPath) {
  const prefix = `${parentPath}.`;
  const suffix = String(childPath ?? "").startsWith(prefix)
    ? String(childPath).slice(prefix.length)
    : "";
  return /^\d+$/u.test(suffix) ? Number.parseInt(suffix, 10) : Number.NaN;
}

/**
 * Projects exact raw-to-compacted bindings through the canonical normalizer's
 * retained-child trace. The normalizer owns deduplication; this boundary only
 * carries its chosen source occurrence forward to the final semantic path.
 */
export function projectNormalizedSearchExpressionSourceBindings(
  sourceText,
  rawExpressionTree,
  compactedExpression,
  expression,
  normalizationOrigins,
) {
  const source = String(sourceText ?? "");
  const compactedBindings = new Map();
  alignNodes(compactedBindings, source, rawExpressionTree, compactedExpression, "root");
  const bindings = new Map();
  if (!(normalizationOrigins instanceof Map)) {
    return bindings;
  }
  for (const [expressionPath, originPath] of normalizationOrigins) {
    addProjectedBinding(bindings, expressionPath, compactedBindings.get(originPath));
  }
  visitExpressionGroups(expression, "root", (_group, groupPath, children) => {
    const groupOrigin = normalizationOrigins.get(groupPath);
    if (!groupOrigin) {
      return;
    }
    for (let index = 1; index < children.length; index += 1) {
      const rightOrigin = normalizationOrigins.get(`${groupPath}.${index}`);
      const separatorIndex = immediateOriginChildIndex(groupOrigin, rightOrigin);
      if (!Number.isInteger(separatorIndex) || separatorIndex <= 0) {
        continue;
      }
      addProjectedBinding(
        bindings,
        searchExpressionOperatorBindingKey(groupPath, index),
        compactedBindings.get(
          searchExpressionOperatorBindingKey(groupOrigin, separatorIndex),
        ),
      );
    }
  });
  return bindings;
}

export function serializeSearchExpressionSourceBindings(bindings, sourceText, expression) {
  const targets = collectExpressionBindingTargets(expression);
  const entries = bindings instanceof Map ? [...bindings.entries()]
    .map(([expressionPath, binding]) => {
      const target = targets.get(expressionPath);
      return {
        expressionPath,
        rawPath: binding?.rawPath,
        targetKey: target?.targetKey,
        sourceText: binding?.sourceText,
        sourceStart: binding?.sourceStart,
        sourceEnd: binding?.sourceEnd,
        ...(binding?.implicit === true || binding?.implicit === false
          ? { implicit: binding.implicit }
          : {}),
      };
    })
    .filter((entry) =>
      entry.expressionPath &&
      entry.rawPath &&
      entry.targetKey &&
      entry.sourceText &&
      Number.isInteger(entry.sourceStart) &&
      Number.isInteger(entry.sourceEnd) &&
      entry.sourceEnd > entry.sourceStart
    )
    .sort((left, right) =>
      left.sourceStart - right.sourceStart || left.sourceEnd - right.sourceEnd
    ) : [];
  return {
    version: RESOLVED_BINDINGS_VERSION,
    source: String(sourceText ?? ""),
    expressionKey: searchExpressionNodeKey(expression),
    entries,
  };
}

/**
 * Reads the parser's validated source-binding sidecar. The raw tree owns source
 * provenance and the resolved tree owns map meaning; presentation never
 * re-resolves labels or guesses alignment from the two final tree shapes.
 */
export function buildSearchExpressionSourceBindings(sourceText, rawExpressionTree, expression) {
  const source = String(sourceText ?? "");
  if (!source.trim() || !isExpressionNode(rawExpressionTree) || !isExpressionNode(expression)) {
    return new Map();
  }
  return resolvedBindingsFromRawTree(source, rawExpressionTree, expression) || new Map();
}
